/**
 * Datasets API Service
 *
 * Handles all dataset-related API calls
 */

import { AI_OPERATIONS_API_URL } from './config';
import { demoModeManager } from './demoModeManager';
import { getAIOpsMockData } from './mockData';

const API_BASE_URL = `${AI_OPERATIONS_API_URL}/genaiops`;

const isDemoActive = (): boolean => demoModeManager.getDemoMode().isDemoMode;

/**
 * Get the selected client ID from localStorage
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

const getSelectedProjectId = (): string | null => {
  return localStorage.getItem('selectedProjectId');
};

/**
 * Append client_id to URL if available (for partner/aws/admin users)
 */
const appendClientId = (url: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}client_id=${clientId}`;
};

const appendProjectId = (url: string): string => {
  try {
    const stored = localStorage.getItem('globalFilter');
    if (stored) {
      const { filterType, selectedId } = JSON.parse(stored);
      if (selectedId) {
        const separator = url.includes('?') ? '&' : '?';
        if (filterType === 'project') return `${url}${separator}project_id=${selectedId}`;
        if (filterType === 'workload') return `${url}${separator}workload_id=${selectedId}`;
        if (filterType === 'account') return `${url}${separator}account_id=${selectedId}`;
        if (filterType === 'environment') return `${url}${separator}environment_id=${selectedId}`;
      }
    }
  } catch { /* ignore */ }
  const projectId = getSelectedProjectId();
  if (!projectId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}project_id=${projectId}`;
};

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

export interface ListDatasetsParams {
  page?: number;
  limit?: number;
  searchTerm?: string;
  tags?: string;
  status?: string;
}

/**
 * List datasets with pagination and filtering
 */
export const listDatasets = async (params: ListDatasetsParams = {}): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(appendProjectId(appendClientId('/genaiops/datasets')));
  const {
    page = 1,
    limit = 50,
    searchTerm = '',
    tags = '',
    status = 'active'
  } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(searchTerm && { searchTerm }),
    ...(tags && { tags }),
    ...(status && { status })
  });

  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets?${queryParams}`)), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch datasets: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a single dataset by ID
 */
export const getDataset = async (datasetId: string): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(appendProjectId(appendClientId(`/genaiops/datasets/${datasetId}`)));
  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}`)), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch dataset: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new dataset
 */
export const createDataset = async (datasetData: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: 'demo-ds-' + Date.now(), ...datasetData };
  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets`)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(datasetData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create dataset: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an existing dataset
 */
export const updateDataset = async (datasetId: string, updates: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: datasetId, ...updates };
  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}`)), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to update dataset: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete a dataset (soft delete)
 */
export const deleteDataset = async (datasetId: string): Promise<any> => {
  if (isDemoActive()) return { success: true };
  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}`)), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete dataset: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Clone an existing dataset
 */
export const cloneDataset = async (datasetId: string, newName: string): Promise<any> => {
  if (isDemoActive()) return { id: 'demo-ds-clone-' + Date.now(), name: newName };
  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}/clone`)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_name: newName }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to clone dataset: ${response.statusText}`);
  }

  return response.json();
};
